import { Injectable } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { BehaviorSubject, Subject } from "rxjs";
import { AppStorageService } from "shared-service/app-storage/app.storage";

export type NotificationRealtimeState = "disconnected" | "connecting" | "connected" | "reconnecting" | "unbound";

@Injectable({ providedIn: "root" })
export class NotificationRealtimeService {
    private static readonly RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000];

    private connection?: signalR.HubConnection;
    private startPromise?: Promise<void>;
    private readonly notificationSubject = new Subject<any>();
    private readonly unreadCountSubject = new Subject<number>();
    private readonly stateSubject = new BehaviorSubject<NotificationRealtimeState>("disconnected");

    readonly notification$ = this.notificationSubject.asObservable();
    readonly unreadCount$ = this.unreadCountSubject.asObservable();
    readonly state$ = this.stateSubject.asObservable();

    constructor(private storage: AppStorageService) {}

    start(): Promise<void> {
        if (this.connection?.state === signalR.HubConnectionState.Connected) return Promise.resolve();
        if (this.startPromise) return this.startPromise;

        this.stateSubject.next("connecting");
        const systemId = this.storage.systemId;
        this.connection = new signalR.HubConnectionBuilder()
            // BFF: không còn access token ở FE — SignalR same-origin gửi kèm cookie httpOnly, BE đổi
            // cookie→Bearer server-side (kể cả cho /notification-hub). accessTokenFactory để rỗng.
            .withUrl("/notification-hub", {
                accessTokenFactory: () => "",
                withCredentials: true,
                headers: systemId ? { "X-System-Id": String(systemId) } : {},
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (context) => {
                    const base = NotificationRealtimeService.RECONNECT_DELAYS_MS[context.previousRetryCount];
                    if (base === undefined) return null;
                    // Spread reconnects from many tabs instead of hammering the service at fixed instants.
                    return base + Math.floor(Math.random() * Math.max(500, Math.floor(base * 0.25)));
                },
            })
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        this.connection.on("NotificationReceived", (payload: any) => this.notificationSubject.next(payload));
        this.connection.on("UnreadCountChanged", (count: number) => this.unreadCountSubject.next(Number(count ?? 0)));
        this.connection.on("BindingConfirmed", (accountId: number) => {
            this.stateSubject.next(Number(accountId ?? 0) > 0 ? "connected" : "unbound");
        });
        this.connection.onreconnecting(() => this.stateSubject.next("reconnecting"));
        this.connection.onreconnected(() => {
            this.stateSubject.next("connecting");
            this.confirmBinding();
        });
        this.connection.onclose(() => {
            this.startPromise = undefined;
            this.stateSubject.next("disconnected");
        });

        this.startPromise = this.connection.start()
            .then(() => this.confirmBinding())
            .catch((error) => {
                this.startPromise = undefined;
                this.stateSubject.next("disconnected");
                throw error;
            });
        return this.startPromise;
    }

    private confirmBinding(): void {
        this.connection?.invoke("ConfirmBinding").catch(() => this.stateSubject.next("unbound"));
    }

    async stop(): Promise<void> {
        this.startPromise = undefined;
        const connection = this.connection;
        this.connection = undefined;
        await connection?.stop();
        this.stateSubject.next("disconnected");
    }
}
